import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  clearError,
  sendOtp,
  verifyOtpLogin,
  verifyOtpRegister,
} from '../store/slices/authSlice';
import { OtpPurpose } from '../types/api';
import { AurieqLogo } from '../components/Brand/AurieqLogo';
import AuthPageShell from '../components/Auth/AuthPageShell';
import { BRAND } from '../constants/branding';
import { authCardSx, authFieldSx, gradientButtonSx } from '../constants/brandStyles';

interface VerifyLocationState {
  purpose?: OtpPurpose;
  email?: string;
  username?: string;
  password?: string;
}

const codeSchema = yup.object({
  code: yup
    .string()
    .required('Verification code is required')
    .matches(/^\d{4,8}$/, 'Enter the numeric code from your email'),
});

const loginEmailSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
});

export const VerifyOtpPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  const state = (location.state || {}) as VerifyLocationState;
  const purpose: OtpPurpose = state.purpose || 'login';
  const isRegister = purpose === 'register';

  const [email, setEmail] = React.useState(state.email || '');
  const [codeSent, setCodeSent] = React.useState(isRegister);
  const [devCode, setDevCode] = React.useState<string | null>(null);
  const [resendSeconds, setResendSeconds] = React.useState(0);

  const emailForm = useForm<{ email: string }>({
    resolver: yupResolver(loginEmailSchema),
    mode: 'onChange',
    defaultValues: { email: state.email || '' },
  });

  const codeForm = useForm<{ code: string }>({
    resolver: yupResolver(codeSchema),
    mode: 'onChange',
    defaultValues: { code: '' },
  });

  React.useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  React.useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  React.useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  const handleSendOtp = async (targetEmail: string) => {
    const result = await dispatch(sendOtp({ email: targetEmail, purpose })).unwrap();
    setEmail(targetEmail);
    setCodeSent(true);
    setResendSeconds(60);
    if (result.dev_code) setDevCode(result.dev_code);
  };

  const onSendLoginCode = emailForm.handleSubmit(async (data) => {
    try {
      await handleSendOtp(data.email);
    } catch (sendError) {
      console.error('Failed to send OTP:', sendError);
    }
  });

  const onResend = async () => {
    if (!email || resendSeconds > 0) return;
    try {
      await handleSendOtp(email);
    } catch (sendError) {
      console.error('Failed to resend OTP:', sendError);
    }
  };

  const onVerify = codeForm.handleSubmit(async (data) => {
    try {
      if (isRegister) {
        if (!state.password) {
          navigate('/register');
          return;
        }
        await dispatch(
          verifyOtpRegister({
            email,
            code: data.code,
            password: state.password,
            username: state.username,
          })
        ).unwrap();
      } else {
        await dispatch(verifyOtpLogin({ email, code: data.code })).unwrap();
      }
    } catch (verifyError) {
      console.error('OTP verification failed:', verifyError);
    }
  });

  return (
    <AuthPageShell>
      <Box sx={authCardSx}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <AurieqLogo variant="full" height={72} to="/" />
        </Box>
        <Typography variant="h5" align="center" gutterBottom sx={{ color: BRAND.colors.text }}>
          {isRegister ? 'Verify your email' : 'Sign in with email code'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {devCode && <Alert severity="info" sx={{ mb: 2 }}>Development code: {devCode}</Alert>}

        {!isRegister && !codeSent && (
          <Box component="form" onSubmit={onSendLoginCode} noValidate>
            <TextField
              {...emailForm.register('email')}
              fullWidth
              label="Email Address"
              type="email"
              margin="normal"
              autoFocus
              error={!!emailForm.formState.errors.email}
              helperText={emailForm.formState.errors.email?.message}
              sx={authFieldSx}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ ...gradientButtonSx, mt: 3, mb: 2 }}
              disabled={!emailForm.formState.isValid || loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send verification code'}
            </Button>
          </Box>
        )}

        {codeSent && (
          <Box component="form" onSubmit={onVerify} noValidate>
            <TextField fullWidth label="Email" value={email} margin="normal" InputProps={{ readOnly: true }} sx={authFieldSx} />
            <TextField
              {...codeForm.register('code')}
              fullWidth
              label="Verification code"
              margin="normal"
              autoFocus
              inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
              error={!!codeForm.formState.errors.code}
              helperText={codeForm.formState.errors.code?.message || 'Check your inbox for a 6-digit code'}
              sx={authFieldSx}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ ...gradientButtonSx, mt: 3, mb: 1 }}
              disabled={!codeForm.formState.isValid || loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : isRegister ? 'Create account' : 'Sign in'}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={onResend}
              disabled={loading || resendSeconds > 0}
              sx={{ color: BRAND.colors.textMuted }}
            >
              {resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : 'Resend code'}
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 2, borderColor: BRAND.colors.border }} />
        <Box textAlign="center">
          <Link component={RouterLink} to="/login" sx={{ color: BRAND.colors.primary }}>
            Back to sign in
          </Link>
        </Box>
      </Box>
    </AuthPageShell>
  );
};

export default VerifyOtpPage;
